import { ReactNode, forwardRef } from 'react';

import { makeStyles } from '@material-ui/core';

import { HeaderPageBreadcrumb, HeaderTab } from '@backstage/ui';

import { useIsEmbedded } from '../../behaviors/confidence-plugin-core';
import { ScrollTopButton } from '../ScrollTopButton';
import { Content } from './Content';
import { Header, HeaderProps } from './Header';
import { HeaderContent } from './HeaderContent';
import { NarrowBoxProps } from './NarrowBox';
import { Page, PageProps } from './Page';
import { PortalPageLayout } from './PortalPageLayout';
import { SidebarContent, SidebarContentProps } from './SidebarContent';

const useStyles = makeStyles(theme => ({
  content: { paddingBottom: theme.spacing(12) },
}));

type PortalPageLayoutProps = {
  breadcrumbs?: HeaderPageBreadcrumb[];
  customActions?: ReactNode[];
  floatingHeader?: ReactNode;
  menuItems?: { value: string; label: string; onClick: () => void }[];
  pageTitle?: string;
  subheader?: ReactNode;
  tabs?: HeaderTab[];
};

export type PageLayoutProps = PageProps &
  Pick<
    HeaderProps,
    | 'title'
    | 'headerBreadcrumbs'
    | 'headerTabs'
    | 'stickyHeader'
    | 'stickyHeaderTabs'
    | 'headerTabsRef'
    | 'isLoading'
    | 'pageTitleOverride'
    | 'enableMargin'
  > &
  Pick<SidebarContentProps, 'sideBarRight' | 'sideBarLeft'> & {
    narrow?: boolean | NarrowBoxProps['width'];
    headerContent?: ReactNode;
    scrollButton?: boolean;
    noPadding?: boolean;
  } & PortalPageLayoutProps;

export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      breadcrumbs,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      title,
      enableMargin,
      headerBreadcrumbs,
      headerTabs,
      headerContent,
      stickyHeader,
      stickyHeaderTabs,
      headerTabsRef,
      isLoading,
      pageTitleOverride,
      scrollButton = false,
      narrow,
      noPadding,
      menuItems,
      tabs,
      pageTitle,
      subheader,
      customActions,
    },
    ref,
  ) => {
    const classes = useStyles();
    const { isEmbedded } = useIsEmbedded();
    const hasMenuItems = menuItems && menuItems.length > 0;

    if (isEmbedded) {
      return (
        <PortalPageLayout
          breadcrumbs={breadcrumbs}
          children={children}
          customActions={customActions || headerContent}
          menuItems={hasMenuItems ? menuItems : undefined}
          sideBarLeft={sideBarLeft}
          sideBarRight={sideBarRight}
          subheader={subheader}
          tabs={tabs}
          pageTitle={typeof title === 'string' ? title : (pageTitle ?? '')}
        />
      );
    }

    return (
      <Page ref={ref}>
        {floatingHeader}
        {scrollButton && <ScrollTopButton />}
        <Header
          enableMargin={enableMargin}
          title={title}
          subheader={subheader}
          isLoading={isLoading}
          pageTitleOverride={pageTitleOverride}
          narrow={narrow}
          headerBreadcrumbs={headerBreadcrumbs}
          headerTabs={headerTabs}
          stickyHeader={stickyHeader}
          stickyHeaderTabs={stickyHeaderTabs}
          headerTabsRef={headerTabsRef}
        >
          {headerContent ? (
            <HeaderContent>{headerContent}</HeaderContent>
          ) : null}
        </Header>

        <Content
          narrow={narrow}
          className={noPadding ? undefined : classes.content}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </Page>
    );
  },
);
